import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR")} €`;

function Countdown({ iso }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const diff = new Date(iso) - new Date();
      if (diff <= 0) { setLeft("expiré"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLeft(`${h}h ${m.toString().padStart(2, "0")}m`);
    };
    tick(); const t = setInterval(tick, 30000); return () => clearInterval(t);
  }, [iso]);
  return <span data-testid="pay-countdown">{left}</span>;
}

export default function PayPage() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [polling, setPolling] = useState(!!sessionId);

  const load = useCallback(() => {
    api.get(`/pay/${token}`).then(({ data }) => setInfo(data)).catch((e) => setErr(e?.response?.data?.detail || "Lien introuvable"));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Poll after Stripe return
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setConfirmed(true); setPolling(false); load(); return; }
        if (data.status === "expired" || attempts > 24) { setPolling(false); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2500);
    };
    poll();
  }, [sessionId, load]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/pay/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur paiement"); setPaying(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] flex items-center justify-center p-6">
      <div className="text-center" data-testid="pay-error"><AlertTriangle className="mx-auto text-[#8A281E] mb-4" size={40} /><p className="font-clash uppercase tracking-tight text-xl">{err}</p></div>
    </div>
  );
  if (!info) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center font-mono-hs text-[#BC5D2E]">// chargement...</div>;

  const expired = info.status === "Option expirée";
  const fullyPaid = info.paiement_complet || confirmed;
  const balanceToPay = info.acompte_paye && !info.is_full && !info.paiement_complet;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] py-16 px-6">
      <div className="max-w-xl mx-auto" data-testid="pay-page">
        <div className="font-clash font-bold text-2xl uppercase tracking-tight mb-1">Hors<span className="text-[#BC5D2E]">Signal</span></div>
        <div className="font-mono-hs text-[10px] text-[#737373] tracking-widest uppercase mb-10">Espace de confirmation</div>

        {fullyPaid ? (
          <div className="border border-[#4ade80]/30 bg-[#4ade80]/5 p-8 text-center" data-testid="pay-confirmed">
            <CheckCircle2 className="mx-auto text-[#4ade80] mb-4" size={48} />
            <h1 className="font-clash text-2xl uppercase tracking-tight mb-2">Paiement confirmé</h1>
            <p className="text-[#A3A3A3] text-sm">Votre place est définitivement réservée. Un email de confirmation vient de vous être envoyé.</p>
          </div>
        ) : (
          <>
            <h1 className="font-clash text-3xl uppercase tracking-tight mb-2">Bonjour {info.first_name || info.name},</h1>
            <p className="text-[#A3A3A3] mb-8">{expired ? "Votre option de réservation a expiré." : balanceToPay ? "Votre acompte est réglé. Il reste le solde à payer pour finaliser." : "Votre candidature a été acceptée. Confirmez votre place ci-dessous."}</p>

            <div className="border border-white/10 bg-[#141414] p-6 space-y-3 mb-6">
              <div className="flex justify-between"><span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Expédition</span><span className="text-sm">{typeof info.expedition_title === "object" ? info.expedition_title.fr : info.expedition_title}</span></div>
              <div className="flex justify-between"><span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Départ</span><span className="text-sm">{info.departure_date || "À définir"}</span></div>
              <div className="flex justify-between"><span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Participants</span><span className="text-sm">{info.participants}</span></div>
              <div className="flex justify-between"><span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">Prix total</span><span className="text-sm">{eur(info.amount_total)}</span></div>
              <div className="border-t border-white/10 pt-3 flex justify-between items-end">
                <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#BC5D2E]">{balanceToPay ? "Solde à régler" : info.is_full ? "Montant à régler (100%)" : "Acompte à régler (30%)"}</span>
                <span className="font-clash text-3xl text-[#BC5D2E]" data-testid="pay-amount">{eur(balanceToPay ? info.balance_due : info.amount_now)}</span>
              </div>
              {!info.is_full && !balanceToPay && info.balance_due > 0 && (
                <p className="font-mono-hs text-[10px] text-[#737373]">Solde de {eur(info.balance_due)} à régler avant le {info.balance_deadline}.</p>
              )}
            </div>

            {!expired && info.expires_at && !balanceToPay && (
              <div className="flex items-center gap-2 text-[#BC5D2E] text-sm mb-6"><Clock size={16} /> Place réservée pendant encore <Countdown iso={info.expires_at} /></div>
            )}

            {expired ? (
              <div className="border border-[#8A281E]/40 bg-[#8A281E]/5 p-5 text-center text-sm text-[#A3A3A3]" data-testid="pay-expired">Ce lien a expiré et la place a été remise à disposition. Contactez-nous à contact@horssignal.com pour le réactiver.</div>
            ) : polling ? (
              <div className="text-center font-mono-hs text-[#BC5D2E] py-4" data-testid="pay-polling">// vérification du paiement en cours...</div>
            ) : (
              <button onClick={pay} disabled={paying} data-testid="pay-btn" className="w-full bg-[#BC5D2E] text-white px-8 py-4 uppercase tracking-widest text-sm hover:bg-[#8A281E] transition-colors disabled:opacity-50">
                {paying ? "..." : `Régler ${eur(balanceToPay ? info.balance_due : info.amount_now)} en toute sécurité`}
              </button>
            )}
            <div className="flex items-center justify-center gap-2 text-[#737373] text-xs mt-4"><ShieldCheck size={14} /> Paiement sécurisé par Stripe</div>
          </>
        )}

        {(info.payment_history || []).length > 0 && (
          <div className="mt-10 border-t border-white/10 pt-6" data-testid="pay-history">
            <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-3">Historique des paiements</div>
            {info.payment_history.map((h, i) => (
              <div key={i} className="flex justify-between text-sm py-1"><span className="text-[#A3A3A3]">{h.type === "deposit" ? "Acompte" : h.type === "balance" ? "Solde" : "Paiement"}</span><span>{eur(h.amount)}</span></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
